import {
  ExportController_parkItemsCsv_v1,
  InvestigationController_parkItems_v1,
  type ParkItemActivityReportResource,
} from '@repo/api-client';
import { Button } from '@repo/ui/components/button';
import { Label } from '@repo/ui/components/label';
import { Switch } from '@repo/ui/components/switch';
import { IconDownload, IconLoader2 } from '@tabler/icons-react';
import { type ReactNode, useEffect, useId, useState } from 'react';

import { useApiClient } from '@/api/api-client';
import { isForbidden, messageForFailure } from '@/api/failure';
import { systemClock } from '@/lib/clock';

import { DayPicker } from '../back-office/DayPicker';
import { EmptyState, LoadFailed, Screen, ScreenFact, ScreenNote } from '../back-office/Screen';
import { formatBusinessDate, fromCalendarDate, toCalendarDate } from '../tickets/business-date';
import { saveDownloadedFile } from './cash-book-export';
import {
  ITEMS_DEFAULT_FILENAME,
  type ItemsCsvTotals,
  readItemsCsv,
  totalsOfItemsCsv,
} from './items-export';
import {
  currentBusinessDate,
  defaultPeriod,
  periodProblem,
  type ReportingPeriod,
} from './reporting-period';

/**
 * The park's item activity as a file: `GET /v1/reports/items.csv`.
 *
 * ## This screen is the export, and it is not the items screen
 *
 * There is no table of items here. `GET /v1/reports/items` — the JSON read this
 * export renders — is `Items.tsx` beside this file, which landed from #185 while
 * this was in flight on a branch it could not see.
 *
 * **The export button belongs on that screen and should move there**, at which
 * point this route can go. It is not moved here because that is a change to a
 * screen another ticket had open, made on its behalf; the two entries sit next
 * to each other in the navigation margin at 170 and 180 in the meantime, which
 * is where somebody reading the items screen will look for its export.
 *
 * What is here instead is the smallest honest thing an export screen owes: the
 * period, what the file will hold for it, and what the file that arrived
 * actually held. The figures shown are the three the disclosure is recorded as —
 * see below — and no others: this screen sums nothing, because the item read
 * publishes no aggregate over its rows and a total invented here would be a
 * figure that exists in one browser and nowhere else.
 *
 * ## Every fetch of the CSV is a recorded act
 *
 * `ExportService.openParkItems` opens a transaction, reads, and writes an
 * **append-only audit entry** naming the actor, the report, the period, and how
 * much left the system as `itemCount`, `dayCount` and `breakdownRowCount`. It is
 * the one "read" in this epic that writes; the JSON read beside it writes
 * nothing, because "a read is not audited".
 *
 * So the two routes are used differently on purpose:
 *
 * - **The JSON read runs on load** and on every change of the period or the
 *   breakdown switch. It costs nothing but a query.
 * - **The CSV is fetched from a click handler and nowhere else.** No pre-fetch,
 *   nothing on hover, nothing on focus, nothing on mount, and **no automatic
 *   retry** — a failed export is offered again as a button. A retry loop would
 *   record one person as having disclosed the park's buying position five times
 *   when they asked once, and throwing the bytes away does not unwrite an entry
 *   in an append-only table.
 *
 * ## Owner only, and the screen still handles being refused
 *
 * Both routes are `@RequireRole('OWNER')`, because every item row carries
 * `costPaise`, `refundedCostPaise`, `voidedCostPaise` and `marginPaise`. `role`
 * is restored from `sessionStorage` and is attacker-editable, so the route table
 * decides what is drawn and the server decides what is served — and
 * `AUTH_FORBIDDEN` arriving is a state below.
 *
 * ## The reconciliation
 *
 * The file states its own totals on its last line, so the acceptance bullet
 * "a downloaded file's totals reconcile against the on-screen figures" is
 * checked rather than asserted: after the file is saved, its bytes are read back
 * through `items-export.ts` and its `TOTAL` row, `DAY` rows and `ITEM_DAY` rows
 * are set against the figures the screen was showing when the button was
 * pressed. A disagreement is reported plainly. **The file is saved either way**
 * — the bytes are what arrived, and withholding them would leave somebody with
 * no file and no way to look at what was wrong with it.
 */

/** Every state the figures can be in. One value, so two cannot coexist. */
type ReadState =
  /** The period is half-chosen or refused, so there is nothing to ask for. */
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly report: ParkItemActivityReportResource }
  | { readonly kind: 'failed'; readonly error: unknown };

/** What the screen says the period holds, and what the audit entry will record. */
interface ExpectedSizes {
  readonly itemCount: number;
  readonly dayCount: number;
  readonly breakdownRowCount: number;
}

/** Where the export has got to. The failure is worded, so it is carried. */
type ExportState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'running' }
  | {
      readonly kind: 'saved';
      readonly filename: string;
      /** What the saved file said about itself, or `null` where it was incomplete. */
      readonly totals: ItemsCsvTotals | null;
      /** The figures on screen at the moment it was asked for. */
      readonly expected: ExpectedSizes;
      /** Whether the bytes were an item export at all. */
      readonly readable: boolean;
    }
  | { readonly kind: 'failed'; readonly error: unknown };

/** What the period holds, counted from the read and never summed. */
function sizesOf(report: ParkItemActivityReportResource): ExpectedSizes {
  return {
    itemCount: report.items.length,
    dayCount: report.period.days.length,
    breakdownRowCount: report.items.reduce((total, item) => total + (item.days?.length ?? 0), 0),
  };
}

export function ItemExport() {
  const client = useApiClient();

  // The period the figures are read over, and the period the pickers show. The
  // same value until somebody starts changing it: a half-cleared range is not a
  // request, so the last complete one stays on screen while a new one is chosen.
  const [period, setPeriod] = useState<ReportingPeriod>(() => defaultPeriod(systemClock));

  /**
   * Whether to ask for the per-business-day breakdown.
   *
   * It drives the read **and** the export together. A screen showing range-level
   * figures while exporting a breakdown would be reconciling a file against a
   * report of something else — and the file is materially more disclosure, which
   * is why the audit entry counts its rows separately.
   */
  const [byDay, setByDay] = useState(false);

  const [state, setState] = useState<ReadState>({ kind: 'loading' });

  // A counter rather than a boolean, so two retries in a row are two distinct
  // values and the second one still fires.
  const [reloads, setReloads] = useState(0);

  const [exporting, setExporting] = useState<ExportState>({ kind: 'idle' });

  const breakdownLabelId = useId();

  const problem = periodProblem(period, systemClock);

  const complete = period.from !== '' && period.to !== '' && problem === null;

  // The park's own day, in Asia/Kolkata — not the device's. Read once per render
  // rather than held in state: a screen left open across midnight should bound
  // itself at the new day, not at the day it opened on.
  const today = currentBusinessDate(systemClock);

  useEffect(() => {
    // A half-filled or refused period is not a request: the notice under the
    // pickers is already saying so, and sending it would earn a 422 for
    // something the screen can see.
    if (!complete) {
      setState({ kind: 'idle' });

      return;
    }

    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    client
      .request(InvestigationController_parkItems_v1, {
        // `byDay` omitted rather than sent as `false` — see `exportQuery`.
        query: { from: period.from, to: period.to, ...(byDay ? { byDay: true } : {}) },
        signal: controller.signal,
      })
      .then((report) => {
        if (live) setState({ kind: 'loaded', report });
      })
      .catch((error: unknown) => {
        // An abort is this screen's own doing — a filter changed, or it
        // unmounted — and is not a failure to report.
        if (live && !(error instanceof DOMException && error.name === 'AbortError')) {
          setState({ kind: 'failed', error });
        }
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, period.from, period.to, byDay, complete, reloads]);

  /**
   * The file, and nothing else in this component fetches it.
   *
   * **`byDay` is omitted rather than sent as `false`.** The API accepts both and
   * answers the same bytes, but "a reader who does not ask for the breakdown
   * gets a byte-identical file" is a promise about a request that did not carry
   * the parameter, and the audit entry records what was asked for. Not asking is
   * how you do not ask.
   *
   * The bytes are saved first and read back second: somebody pressed a button to
   * get a file, and the reconciliation below is a report about the file rather
   * than a condition on producing it.
   */
  const runExport = () => {
    if (state.kind !== 'loaded' || !complete || exporting.kind === 'running') return;

    const expected = sizesOf(state.report);

    setExporting({ kind: 'running' });

    client
      .download(ExportController_parkItemsCsv_v1, {
        query: { from: period.from, to: period.to, ...(byDay ? { byDay: true } : {}) },
      })
      .then(async (file) => {
        const filename = file.filename?.trim() ?? '';

        saveDownloadedFile(file, ITEMS_DEFAULT_FILENAME);

        const parsed = readItemsCsv(await file.blob.text());

        setExporting({
          kind: 'saved',
          filename: filename === '' ? ITEMS_DEFAULT_FILENAME : filename,
          totals: parsed === null ? null : totalsOfItemsCsv(parsed),
          readable: parsed !== null,
          expected,
        });
      })
      .catch((error: unknown) => {
        setExporting({ kind: 'failed', error });
      });
  };

  const sizes = state.kind === 'loaded' ? sizesOf(state.report) : null;

  return (
    <Screen
      title="Item export"
      lede="Item activity as a file"
      action={
        <Button
          onClick={runExport}
          // Nothing to export until the figures are in hand: the button says what
          // will leave the system, and a press before that is a press against a
          // period the screen has not read yet.
          disabled={state.kind !== 'loaded' || exporting.kind === 'running'}
        >
          {exporting.kind === 'running' ? (
            <IconLoader2 data-icon="inline-start" className="animate-spin" />
          ) : (
            <IconDownload data-icon="inline-start" />
          )}
          {exporting.kind === 'running' ? 'Exporting…' : 'Export CSV'}
        </Button>
      }
      aside={
        <>
          <ScreenFact label="Period">
            {period.from === '' || period.to === ''
              ? 'Choose both days.'
              : `${formatBusinessDate(period.from)} to ${formatBusinessDate(period.to)}`}
          </ScreenFact>

          <div className="space-y-2">
            <Label htmlFor="item-export-from" className="text-sm text-muted-foreground">
              From
            </Label>
            <DayPicker
              id="item-export-from"
              value={period.from}
              emptyLabel="Choose a day"
              format={formatBusinessDate}
              fromDate={fromCalendarDate}
              toDate={toCalendarDate}
              maxDay={today}
              onValueChange={(next) => {
                setPeriod((held) => ({ ...held, from: next }));
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="item-export-to" className="text-sm text-muted-foreground">
              To
            </Label>
            <DayPicker
              id="item-export-to"
              value={period.to}
              emptyLabel="Choose a day"
              format={formatBusinessDate}
              fromDate={fromCalendarDate}
              toDate={toCalendarDate}
              // The route refuses a period ending after the park's current
              // trading day, so a day past it is not offered. The notice below
              // still stands — see `maxDay` on `DayPicker` for why both.
              maxDay={today}
              onValueChange={(next) => {
                setPeriod((held) => ({ ...held, to: next }));
              }}
            />
          </div>

          <div className="flex items-center gap-3">
            <Switch
              id="item-export-by-day"
              aria-labelledby={breakdownLabelId}
              checked={byDay}
              onCheckedChange={(checked) => {
                setByDay(checked);
                // The saved report is about the file that was asked for. Change
                // what would be asked for and it is a statement about something
                // else, so it goes rather than sitting there looking current.
                setExporting({ kind: 'idle' });
              }}
            />
            <Label
              id={breakdownLabelId}
              htmlFor="item-export-by-day"
              className="cursor-pointer text-sm"
            >
              Include the per-day breakdown
            </Label>
          </div>

          {/* Under the pickers rather than over the figures: it is about the two
              controls immediately above it, and it is not a failed read. */}
          {problem !== null && (
            <p role="status" className="text-sm text-destructive">
              {problem}
            </p>
          )}

          {/* A control-level failure goes where the control is, not in a toast:
              toasts vanish and somebody who looked away cannot get it back. */}
          {exporting.kind === 'failed' && (
            <p role="status" className="text-sm text-destructive">
              The export could not be made. {messageForFailure(exporting.error)}
            </p>
          )}
        </>
      }
      guidance={
        <>
          <ScreenNote label="This download is recorded">Under your name.</ScreenNote>

          <ScreenNote label="Money in the file is paise">1700000 is ₹17,000.00.</ScreenNote>
        </>
      }
    >
      {state.kind === 'idle' && (
        <EmptyState title="Choose the period to export" description="Choose both days." />
      )}

      {state.kind === 'loading' && (
        // A skeleton at roughly the height the figures settle at, so the page
        // does not jump when they land. `aria-hidden`: there is nothing to read.
        <div aria-hidden className="h-56 animate-pulse rounded-2xl bg-muted" />
      )}

      {state.kind === 'failed' &&
        (isForbidden(state.error) ? (
          <EmptyState
            title="This account cannot read the park’s item activity"
            description="If you are the owner, sign out and back in."
          />
        ) : (
          <LoadFailed
            error={state.error}
            onRetry={() => {
              setReloads((count) => count + 1);
            }}
            title="These figures could not be read"
          />
        ))}

      {state.kind === 'loaded' && sizes !== null && (
        <div className="space-y-8">
          <Band name="What this export will contain">
            {sizes.itemCount === 0 && sizes.dayCount === 0 && (
              <p role="status" className="mb-4 text-sm text-muted-foreground">
                Nothing traded in the park between these two days.
              </p>
            )}

            <dl>
              <Count term="Items with activity">{sizes.itemCount}</Count>
              <Count term="Business days in the period">{sizes.dayCount}</Count>
              {byDay && <Count term="Breakdown rows">{sizes.breakdownRowCount}</Count>}
            </dl>
          </Band>

          {exporting.kind === 'saved' && <Saved state={exporting} />}
        </div>
      )}
    </Screen>
  );
}

/**
 * What the file that arrived actually held, set against what was on screen.
 *
 * Split out so the three answers — read and reconciles, read and does not, or
 * not readable at all — sit next to each other and the middle one cannot be
 * quietly folded into the first.
 */
function Saved({ state }: { state: Extract<ExportState, { kind: 'saved' }> }) {
  const { totals, expected, filename, readable } = state;

  const reconciles =
    totals !== null &&
    totals.itemCount === expected.itemCount &&
    totals.dayRows === expected.dayCount &&
    totals.breakdownRows === expected.breakdownRowCount;

  return (
    <Band name="The file you saved">
      <p className="text-sm text-foreground">
        <span className="font-mono">{filename}</span>
      </p>

      {!readable && (
        <p role="status" className="mt-3 text-sm text-destructive">
          The file was saved, but it is not an item export. Open it before filing it.
        </p>
      )}

      {readable && totals === null && (
        <p role="status" className="mt-3 text-sm text-destructive">
          The file was saved, but it does not end on its TOTAL row. Take it again.
        </p>
      )}

      {totals !== null && (
        <>
          <dl className="mt-3">
            <Count term="Items in the file">{totals.itemCount}</Count>
            <Count term="Day rows">{totals.dayRows}</Count>
            {(totals.breakdownRows > 0 || expected.breakdownRowCount > 0) && (
              <Count term="Breakdown rows">{totals.breakdownRows}</Count>
            )}
          </dl>

          <p
            role="status"
            className={`mt-3 text-sm ${reconciles ? 'text-muted-foreground' : 'text-destructive'}`}
          >
            {reconciles
              ? 'It reconciles with the figures above.'
              : 'It does not reconcile with the figures above. Report it; do not edit either.'}
          </p>
        </>
      )}
    </Band>
  );
}

/**
 * A named band of the page, so each is a landmark a reader can jump to.
 *
 * The same heading-and-hairline composition `ProfitAndLoss`, `DayClose` and
 * `TradingSummary` use, and a fourth caller is well past the moment
 * `docs/ui-conventions.md` says to promote it into `back-office/Screen.tsx`.
 * That move touches three screens across two epics while their tickets are in
 * flight, so it is named in the PR body rather than taken here.
 */
function Band({ name, children }: { name: string; children: ReactNode }) {
  const headingId = useId();

  return (
    <section aria-labelledby={headingId}>
      <header className="border-b border-border pb-3">
        <h2 id={headingId} className="font-medium text-foreground">
          {name}
        </h2>
      </header>
      <div className="pt-4">{children}</div>
    </section>
  );
}

/**
 * One counted figure.
 *
 * `numeric` and `font-mono` for the reason `docs/ui-conventions.md` gives:
 * Geist's default figures are proportional, and a column of counts read against
 * another column of counts is exactly the comparison that needs them not to be.
 * **No `formatPaise` anywhere near this** — these are counts of rows, not money.
 */
function Count({ term, children }: { term: string; children: number }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2">
      <dt className="text-muted-foreground">{term}</dt>
      <dd className="text-right font-mono text-foreground numeric">{children}</dd>
    </div>
  );
}
